import { useQuery } from "@apollo/client";
import type {
  Conversation,
  ConversationFilter,
  ConversationsConnection,
  Organization,
  Project,
  Agent,
  Connection,
} from "@casey/db-types";
import { ConversationsOrderBy } from "@casey/db-types";
import { GET_INTERVIEW_SESSIONS } from "../gql";

interface ConversationsData {
  organizationBySlug: Organization & {
    projects: {
      nodes: Array<
        Project & {
          conversations: ConversationsConnection;
        }
      >;
    };
  };
}

interface ConversationsVariables {
  organizationSlug: string;
  first?: number;
  after?: string;
  filter?: ConversationFilter;
  orderBy?: ConversationsOrderBy[];
}

export function useConversations(
  organizationSlug: string,
  options: {
    first?: number;
    after?: string;
    filter?: ConversationFilter;
    orderBy?: ConversationsOrderBy[];
  } = {},
) {
  const {
    first = 20,
    after,
    filter,
    orderBy = [ConversationsOrderBy.CreatedAtDesc],
  } = options;

  const { data, loading, error, refetch, fetchMore } = useQuery<
    ConversationsData,
    ConversationsVariables
  >(GET_INTERVIEW_SESSIONS, {
    variables: {
      organizationSlug,
      first,
      ...(after && { after }),
      ...(filter && { filter }),
      ...(orderBy && { orderBy }),
    },
    skip: !organizationSlug,
    errorPolicy: "all",
  });

  // Flatten sessions from all projects
  const allSessions =
    data?.organizationBySlug?.projects?.nodes?.flatMap(
      (project) =>
        project?.conversations?.edges
          ?.map((edge) => edge.node)
          .filter(
            (node): node is Conversation => node !== null && node !== undefined,
          ) || [],
    ) || [];

  // Get pagination info from the first project (assuming similar pagination across projects)
  const pageInfo =
    data?.organizationBySlug?.projects?.nodes?.[0]?.conversations?.pageInfo;
  const totalCount =
    data?.organizationBySlug?.projects?.nodes?.reduce(
      (sum, project) => sum + (project?.conversations?.totalCount || 0),
      0,
    ) || 0;

  const loadMore = () => {
    if (!pageInfo?.hasNextPage || loading) return;

    fetchMore({
      variables: {
        after: pageInfo.endCursor,
      },
    });
  };

  return {
    sessions: allSessions,
    loading,
    error,
    refetch,
    loadMore,
    hasMore: pageInfo?.hasNextPage || false,
    totalCount,
  };
}

export type { ConversationsData, ConversationsVariables };
